import React, { useEffect, useState } from "react";
import { IClientArchive } from "../../types/archiveClient.type";
import { api } from "../../api/api";
import { Button, Input, message, Modal, Select, Space, Table, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { ClientType } from "../../types/client.type";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function ArchiveClients() {
  const [clients, setClients] = useState<IClientArchive[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [clientType, setClientType] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("dateRemoved");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const fetchClients = async () => {
    setLoading(true);

    try {
      const response = await api.get("/client/archive", {
        params: {
          page,
          limit: pageSize,
          search: search || undefined,
          clientType: clientType || undefined,
          sortBy,
          order: sortOrder,
        },
      });

      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching archived clients", error);
      message.error("Ошибка при получении архивированных клиентов.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, pageSize, search, clientType, sortBy, sortOrder]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Тип клиента",
      dataIndex: "clientType",
      key: "clientType",
    },
    {
      title: "Валюта долга",
      dataIndex: "debtCurrency",
      key: "debtCurrency",
    },
    {
      title: "Долг клиента",
      dataIndex: "totalClientDebt",
      key: "totalClientDebt",
      render: (data: number, record: IClientArchive) => `${roundToThreeDecimals(data)} ${record.debtCurrency}`,
    },
    {
      title: "Наш долг",
      key: "totalCompanyDebt",
      dataIndex: "totalCompanyDebt",
      render: (data: number, record: IClientArchive) => `${roundToThreeDecimals(data)} ${record.debtCurrency}`,
    },
    {
      title: "Дата удаления",
      dataIndex: "dateRemoved",
      key: "dateRemoved",
    },
    {
      title: "Удалил",
      dataIndex: "removedBy",
      key: "removedBy",
    },

  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Архив клиентов (только просмотр)</Title>
      
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
          style={{ width: 200 }}
        />
        <Select
          placeholder="Тип клиента"
          value={clientType}
          onChange={(value) => {
            setClientType(value);
            setPage(1);
          }}
          allowClear
          style={{ width: 150 }}
        >
          {Object.values(ClientType).map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Сортировать по"
          value={sortBy}
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="dateRemoved">Дата удаления</Option>
          <Option value="name">Название</Option>
          <Option value="removedBy">Кто удалил</Option>
        </Select>
        <Select
          value={sortOrder}
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={clients}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
} 